import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from "@mui/material";
import { Warning, Schedule, Info } from "@mui/icons-material";

interface WithdrawalConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => Promise<void>;
  purposeName: string;
  purposeDescription: string;
  loading?: boolean;
  error?: string;
}

const WithdrawalConfirmationDialog: React.FC<WithdrawalConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  purposeName,
  purposeDescription,
  loading = false,
  error,
}) => {
  const [reason, setReason] = useState("");

  const handleConfirm = async () => {
    await onConfirm(reason.trim() || undefined);
  };

  const handleClose = () => {
    if (!loading) {
      setReason("");
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 3,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Warning color="warning" />
          <Typography variant="h6" component="div">
            Confirm Withdrawal Request
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom color="primary">
            Purpose Details
          </Typography>
          <Box
            sx={{
              p: 2,
              bgcolor: "grey.50",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <Typography variant="h6" gutterBottom>
              {purposeName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {purposeDescription}
            </Typography>
          </Box>
        </Box>

        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Important:</strong> Withdrawing consent will stop the processing of your personal data for this purpose. This action will be
            processed within 7 business days.
          </Typography>
        </Alert>

        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>
            Reason for Withdrawal (Optional)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Please provide a reason for withdrawing consent (optional)..."
            variant="outlined"
            disabled={loading}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
        </Box>

        <Box
          sx={{
            p: 2,
            bgcolor: "info.50",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "info.200",
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Schedule color="info" fontSize="small" />
            <Typography variant="subtitle2" color="info.main">
              Processing Timeline
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Your withdrawal request will be processed within <strong>7 business days</strong>. You will receive an email confirmation once the request
            is completed.
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            p: 2,
            bgcolor: "grey.50",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "grey.200",
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Info color="action" fontSize="small" />
            <Typography variant="subtitle2">What happens next?</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            • Your request will be reviewed and processed
            <br />
            • Data processing for this purpose will be stopped
            <br />
            • You will receive email confirmation of completion
            <br />• You can track the status in your portal
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={handleClose} disabled={loading} color="inherit" sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading}
          variant="contained"
          color="warning"
          startIcon={loading ? <CircularProgress size={16} /> : <Warning />}
          sx={{
            minWidth: 140,
            "&:hover": {
              backgroundColor: "warning.dark",
            },
          }}
        >
          {loading ? "Submitting..." : "Submit Request"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WithdrawalConfirmationDialog;
